import { z } from 'zod';

/**
 * Live-conference shapes (M5). A sale (joint conference) or each lot (per-lot conference)
 * is backed by a provider room; the room's join URL is surfaced to bidders over WebSocket
 * (`conference.available`). Recordings are mandatory dispute evidence, pulled into R2 after
 * close. The model is provider-agnostic — `provider` discriminates Zoom (built) from Meet
 * (interface-ready). Mirrors the generic `conference_*` columns + `conference_recordings`
 * table (`docs/03-data-model.md`) and `docs/07-integration-map.md`; see ADR 0018.
 * Timestamps are epoch-ms integers on the wire.
 */

/** Conferencing provider discriminator. Zoom is built in M5; Meet is interface-ready. */
export const conferenceProviderSchema = z.enum(['zoom', 'meet']);
export type ConferenceProvider = z.infer<typeof conferenceProviderSchema>;

/** The provisioned room as surfaced on a sale/lot read. Null fields until provisioned. */
export const conferenceInfoSchema = z.object({
  provider: conferenceProviderSchema.nullable(),
  roomId: z.string().nullable(),
  joinUrl: z.string().nullable(), // attendee join URL
  hostUrl: z.string().nullable(), // operator/host start URL (admin-only surfaces)
  provisionedAt: z.number().int().nullable(), // epoch-ms
});
export type ConferenceInfo = z.infer<typeof conferenceInfoSchema>;

/** Recording metadata projection (`conference_recordings`). No card/PII material. */
export const conferenceRecordingViewSchema = z.object({
  id: z.uuid(),
  saleId: z.uuid(),
  lotId: z.uuid().nullable(), // null for a joint (sale-level) recording
  provider: conferenceProviderSchema,
  status: z.enum(['pending', 'stored', 'failed']),
  r2Key: z.string().nullable(),
  bytes: z.number().int().nullable(),
  durationS: z.number().int().nullable(),
  startedAt: z.number().int().nullable(), // epoch-ms
  fetchedAt: z.number().int().nullable(), // epoch-ms
  expiresAt: z.number().int().nullable(), // epoch-ms; retention horizon
});
export type ConferenceRecordingView = z.infer<typeof conferenceRecordingViewSchema>;
